import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EPSRecordType, FpsRecordType } from '@nxsys/types/models/RTI';
import {
  NotificationMessageType,
  PaginationType,
} from '@nxsys/types/models/Common';
import { rtiAct } from '../action';
import { showToast } from '@nxsys/utils/toast';
import { appIntl } from '@nxsys/helpers/Common';
import { Cis300RecordType } from '@nxsys/types/models/RTI/cis300';
import { GetCIS300ListParams, GetEPSListParams, GetFPSListParams } from '../types';

export interface RTIState {
  loading: boolean;
  normalFPSLoading: boolean;
  leaveFPSLoading: boolean;
  fpsData: {
    list: FpsRecordType[];
    pagination: PaginationType;
    queryParams: GetFPSListParams | null;
  };
  epsData: {
    list: EPSRecordType[];
    pagination: PaginationType;
    queryParams: GetEPSListParams | null;
  };
  cis300Data: {
    list: Cis300RecordType[];
    pagination: PaginationType;
    queryParams: GetCIS300ListParams | null;
  };
  messageNotification: NotificationMessageType | null;
}

const initialState: RTIState = {
  loading: false,
  normalFPSLoading: false,
  leaveFPSLoading: false,
  fpsData: {
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  epsData: {
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  cis300Data: {
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  messageNotification: null,
};

const rtiSlice = createSlice({
  name: 'rti',
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.messageNotification = null;
    },
    setMessage: (
      state,
      action: PayloadAction<{
        text: string;
        type: 'success' | 'error' | 'info';
      }>,
    ) => {
      state.messageNotification = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get FPS Records
    builder.addCase(rtiAct.getFPSRecords.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getFPSRecords.fulfilled, (state, action) => {
      state.loading = false;
      state.fpsData.list = action.payload.data;
      state.fpsData.pagination = action.payload.pagination;
      state.fpsData.queryParams = action.payload.queryParams;
    });
    builder.addCase(rtiAct.getFPSRecords.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.getError' }),
      });
    });

    // Get FPS By Id
    builder.addCase(rtiAct.getFPSById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getFPSById.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.getFPSById.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.getByIdError' }),
      });
    });

    // Delete FPS Record
    builder.addCase(rtiAct.deleteFPSRecord.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.deleteFPSRecord.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'rti.fps.deleteSuccess' }),
      });
    });
    builder.addCase(rtiAct.deleteFPSRecord.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.deleteError' }),
      });
    });

    // Download FPS Record
    builder.addCase(rtiAct.downloadFPSRecord.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.downloadFPSRecord.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.downloadFPSRecord.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.downloadError' }),
      });
    });

    // Download FPS Error
    builder.addCase(rtiAct.downloadFPSError.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.downloadFPSError.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.downloadError' }),
      });
    });

    // Resubmit FPS Record
    builder.addCase(rtiAct.resubmitFPSRecord.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.resubmitFPSRecord.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'rti.fps.resubmitSuccess' }),
      });
    });
    builder.addCase(rtiAct.resubmitFPSRecord.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.resubmitError' }),
      });
    });

    // Export All FPS Error
    builder.addCase(rtiAct.exportAllFPSError.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.exportAllFPSError.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.exportAllFPSError.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.exportAllFPSError' }),
      });
    });

    // Get List Payroll By FPS Id
    builder.addCase(rtiAct.getListPayrollByFPSId.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getListPayrollByFPSId.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.getListPayrollByFPSId.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.getListPayrollByFPSIdError' }),
      });
    });

    // Validate XML
    builder.addCase(rtiAct.validateXML.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.validateXML.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload.success) {
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'common.validXML',
          }),
        });
      } else {
        showToast({
          type: 'error',
          message:
            action.payload?.error ||
            appIntl().formatMessage({ id: 'common.invalidXML' }),
        });
      }
    });
    builder.addCase(rtiAct.validateXML.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'common.invalidXML' }),
      });
    });

    // Get EPS Records
    builder.addCase(rtiAct.getEPSRecords.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getEPSRecords.fulfilled, (state, action) => {
      state.loading = false;
      state.epsData.list = action.payload.data;
      state.epsData.pagination = action.payload.pagination;
      state.epsData.queryParams = action.payload.queryParams;
    });
    builder.addCase(rtiAct.getEPSRecords.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.eps.getError' }),
      });
    });

    // Resubmit EPS
    builder.addCase(rtiAct.resubmitEPS.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.resubmitEPS.fulfilled, (state, action) => {
      state.loading = false;
      if (!action.payload.data || !action.payload.success) {
        showToast({
          type: 'error',
          message:
            action.payload?.data?.message ||
            appIntl().formatMessage({ id: 'rti.eps.resubmitError' }),
        });
        return;
      }
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'rti.eps.resubmitSuccess' }),
      });
    });
    builder.addCase(rtiAct.resubmitEPS.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.eps.resubmitError' }),
      });
    });

    // Get List Payroll By EPS Id
    builder.addCase(rtiAct.getListPayrollByEPSId.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getListPayrollByEPSId.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.getListPayrollByEPSId.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.eps.getListPayrollByEPSIdError' }),
      });
    });

    // Get EPS By Id
    builder.addCase(rtiAct.getEPSById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getEPSById.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.getEPSById.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.eps.getByIdError' }),
      });
    });

    // Export All EPS Error
    builder.addCase(rtiAct.exportAllEPSErrors.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.exportAllEPSErrors.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.exportAllEPSErrors.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.eps.exportAllEPSError' }),
      });
    });

    // Get CIS300 Records
    builder.addCase(rtiAct.getCIS300Records.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getCIS300Records.fulfilled, (state, action) => {
      state.loading = false;
      state.cis300Data.list = action.payload.data;
      state.cis300Data.pagination = action.payload.pagination;
      state.cis300Data.queryParams = action.payload.queryParams;
    });
    builder.addCase(rtiAct.getCIS300Records.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.cis300.getError' }),
      });
    });

    // Get CIS300 Payroll List
    builder.addCase(rtiAct.getCIS300PayrollList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getCIS300PayrollList.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.getCIS300PayrollList.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.cis300.getPayrollListError' }),
      });
    });

    // Delete CIS300 Record
    builder.addCase(rtiAct.deleteCIS300Record.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.deleteCIS300Record.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'rti.cis300.deleteSuccess' }),
      });
    });
    builder.addCase(rtiAct.deleteCIS300Record.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.cis300.deleteError' }),
      });
    });

    // Resubmit CIS300
    builder.addCase(rtiAct.resubmitCIS300.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.resubmitCIS300.fulfilled, (state, action) => {
      state.loading = false;
      if (!action.payload.data || !action.payload.success) {
        showToast({
          type: 'error',
          message:
            action.payload?.data?.message ||
            appIntl().formatMessage({ id: 'rti.cis300.resubmitError' }),
        });
        return;
      }
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'rti.cis300.resubmitSuccess' }),
      });
    });
    builder.addCase(rtiAct.resubmitCIS300.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.cis300.resubmitError' }),
      });
    });

    // Get CIS300 By Id
    builder.addCase(rtiAct.getCIS300ById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getCIS300ById.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.getCIS300ById.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.cis300.getByIdError' }),
      });
    });

    // Get All CIS300 Errors
    builder.addCase(rtiAct.getAllCIS300Errors.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getAllCIS300Errors.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.getAllCIS300Errors.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.cis300.getAllCIS300ErrorsError' }),
      });
    });

    // Get Normal FPS
    builder.addCase(rtiAct.getNormalFPS.pending, (state) => {
      state.normalFPSLoading = true;
    });
    builder.addCase(rtiAct.getNormalFPS.fulfilled, (state) => {
      state.normalFPSLoading = false;
    });
    builder.addCase(rtiAct.getNormalFPS.rejected, (state, action) => {
      state.normalFPSLoading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.getError' }),
      });
    });

    // Get Leave FPS
    builder.addCase(rtiAct.getLeaveFPS.pending, (state) => {
      state.leaveFPSLoading = true;
    });
    builder.addCase(rtiAct.getLeaveFPS.fulfilled, (state) => {
      state.leaveFPSLoading = false;
    });
    builder.addCase(rtiAct.getLeaveFPS.rejected, (state, action) => {
      state.leaveFPSLoading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.fps.getError' }),
      });
    });

    // Submit FPS
    builder.addCase(rtiAct.submitFPS.pending, (state) => {
      state.normalFPSLoading = true;
    });
    builder.addCase(rtiAct.submitFPS.fulfilled, (state) => {
      state.normalFPSLoading = false;
    });
    // The caller toasts the backend message per company, so no toast here.
    builder.addCase(rtiAct.submitFPS.rejected, (state) => {
      state.normalFPSLoading = false;
    });

    // Submit Leaver FPS
    builder.addCase(rtiAct.submitLeaverFPS.pending, (state) => {
      state.leaveFPSLoading = true;
    });
    builder.addCase(rtiAct.submitLeaverFPS.fulfilled, (state) => {
      state.leaveFPSLoading = false;
    });
    // The caller toasts the backend message per company, so no toast here.
    builder.addCase(rtiAct.submitLeaverFPS.rejected, (state) => {
      state.leaveFPSLoading = false;
    });

    // Get Pre Submit EPS
    builder.addCase(rtiAct.getPreSubmitEPS.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.getPreSubmitEPS.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.getPreSubmitEPS.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.eps.getError' }),
      });
    });

    // Save CIS Deductions Suffered
    builder.addCase(rtiAct.epsSaveCisDeductionsSuffered.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.epsSaveCisDeductionsSuffered.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(
      rtiAct.epsSaveCisDeductionsSuffered.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'rti.eps.saveCISDeductionsSufferedError',
            }),
        });
      },
    );

    // Submit EPS
    builder.addCase(rtiAct.submitEPS.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.submitEPS.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rtiAct.submitEPS.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.eps.submitError' }),
      });
    });

    // Delete EPS Record
    builder.addCase(rtiAct.deleteEPSRecord.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.deleteEPSRecord.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'rti.eps.deleteSuccess' }),
      });
    });
    builder.addCase(rtiAct.deleteEPSRecord.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'rti.eps.deleteError' }),
      });
    });

    // Send to SafeRec (FPS / EPS / CIS300)
    builder.addCase(rtiAct.sendSafeRecRTI.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rtiAct.sendSafeRecRTI.fulfilled, (state, action) => {
      state.loading = false;
      const payload = action.payload as any;
      const success = !!payload?.success;
      const type = action.meta.arg?.type;
      const message = success
        ? appIntl().formatMessage({
            id: `common.sendSafeRec${type}Success`,
          }) || 'Sent to SafeRec successfully'
        : appIntl().formatMessage({
            id: `common.sendSafeRec${type}Error`,
          }) || 'Failed to send to SafeRec';
      showToast({
        type: success ? 'success' : 'error',
        message,
      });
    });
    builder.addCase(rtiAct.sendSafeRecRTI.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as any;
      const type = action.meta.arg?.type;
      showToast({
        type: 'error',
        message:
          payload?.response?.data?.message ||
          payload?.message ||
          appIntl().formatMessage({
            id: `common.sendSafeRec${type}Error`,
          }),
      });
    });
  },
});

export const rtiReducer = rtiSlice.reducer;
export const rtiActions = {
  ...rtiSlice.actions,
  ...rtiAct,
};
